import React, { useContext } from 'react';
import { Box, Typography, Button, makeStyles } from '@material-ui/core';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import WarningIcon from '@material-ui/icons/Warning';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { useHistory } from 'react-router-dom';
import { AuthContext } from '../../context/Auth/AuthContext';
import { usePlanPermissions } from '../../context/PlanPermissionsContext';
import { canManageSubscription } from '../../utils/userPaths';

const useStyles = makeStyles(() => ({
  banner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '6px 16px',
    width: '100%',
    boxSizing: 'border-box',
    flexWrap: 'nowrap',
    overflow: 'hidden',
  },
  bannerActive: {
    backgroundColor: '#e8f4fd',
    borderBottom: '1px solid #bcd9f0',
    color: '#1565c0',
  },
  bannerExpiring: {
    backgroundColor: '#fff8e1',
    borderBottom: '1px solid #ffe082',
    color: '#e65100',
  },
  bannerExpired: {
    backgroundColor: '#fdecea',
    borderBottom: '1px solid #f5c6cb',
    color: '#c62828',
  },
  icon: {
    fontSize: 18,
    flexShrink: 0,
  },
  text: {
    fontSize: '0.8125rem',
    fontWeight: 500,
    lineHeight: 1.4,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flexShrink: 1,
    minWidth: 0,
  },
  ctaButton: {
    marginLeft: 8,
    fontSize: '0.75rem',
    padding: '2px 10px',
    flexShrink: 0,
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 6,
  },
}));

/**
 * Banner de trial exibido no topo do layout quando user.trial.inTrial === true.
 * Não aparece quando a conta está bloqueada (gerenciado pelo layout).
 */
const TrialBanner = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { trialActive, trialDaysLeft, trialExpiringSoon } = usePlanPermissions();
  const canManageBilling = canManageSubscription(user);

  if (!trialActive) return null;

  // daysRemaining conta os dias além de hoje: 0 = hoje é o último dia com acesso
  const isLastDay = trialDaysLeft <= 0;
  const isExpiring = trialExpiringSoon;

  let bannerClass = classes.bannerActive;
  let Icon = AccessTimeIcon;
  let message = `Você está no período de teste — ${trialDaysLeft} ${trialDaysLeft === 1 ? 'dia restante' : 'dias restantes'} além de hoje.`;

  if (isLastDay) {
    bannerClass = classes.bannerExpired;
    Icon = ErrorOutlineIcon;
    message = 'Último dia do período de teste. Escolha um plano para continuar.';
  } else if (isExpiring) {
    bannerClass = classes.bannerExpiring;
    Icon = WarningIcon;
    message = `Período de teste acaba em ${trialDaysLeft} ${trialDaysLeft === 1 ? 'dia' : 'dias'}. Escolha um plano para não perder o acesso.`;
  }

  return (
    <Box className={`${classes.banner} ${bannerClass}`}>
      <Icon className={classes.icon} />
      <Typography className={classes.text}>{message}</Typography>
      {canManageBilling && (
        <Button
          variant="outlined"
          size="small"
          className={classes.ctaButton}
          style={{ borderColor: 'currentColor', color: 'inherit' }}
          onClick={() => history.push('/subscription/plans')}
        >
          Ver planos
        </Button>
      )}
    </Box>
  );
};

export default TrialBanner;
